import { JSX, useEffect } from 'react';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { SearchFacet, SearchResult, SearchResults } from '#server/models';
import { Pagination, Spinner, NoResults, SearchError } from '@/components';
import { useFilterStore, useSortOrderStore } from '@/store';

export interface ResultListProps {
  searchResults: SearchResults | undefined;
  loading: boolean;
  error: any;
  setPage: (facet: SearchFacet, page: number) => void;
  facet: SearchFacet;
  resultsHeaderRef: React.RefObject<HTMLElement | null>;
  resultToCardMapper: (result: SearchResult) => JSX.Element | null;
}

export const ResultsList = ({
  searchResults,
  loading,
  error,
  setPage,
  facet,
  resultsHeaderRef,
  resultToCardMapper,
}: ResultListProps) => {
  const { facetFilters } = useFilterStore();
  const { activeSortOrder } = useSortOrderStore();

  const scroll = async () => {
    if (resultsHeaderRef.current) {
      const body = document.body;
      // if body is fixed, just reset the top
      // as we won't be able to scroll
      if (body.style.position === 'fixed') {
        body.style.top = 'auto';
      } else {
        await scrollIntoView(resultsHeaderRef.current, {
          behavior: 'smooth',
          block: 'end',
        });
      }
    }
  };

  const onPageChange = async (page: number) => {
    setPage(facet, page);
    await scroll();
  };

  useEffect(() => {
    return () => {
      if (facetFilters) {
        scroll();
      }
    };
  }, [facetFilters]);

  useEffect(() => {
    return () => {
      if (activeSortOrder) {
        scroll();
      }
    };
  }, [activeSortOrder]);

  const currentPage = searchResults?.page ?? 1;
  const pageSize = searchResults?.pageSize ?? 10;
  const resultCount = searchResults?.resultCount ?? 0;
  const totalPages = Math.ceil(resultCount / pageSize) ?? 0;

  const hasResults = searchResults?.results && searchResults?.results?.length > 0;
  const noResults = searchResults?.results && searchResults?.results?.length === 0;

  return (
    <>
      {error && <SearchError />}
      {!error && loading && <Spinner />}
      {!error && !loading && hasResults && searchResults!.results!.map(resultToCardMapper)}
      {!error && !loading && noResults && <NoResults />}
      {!error && !loading && hasResults && (
        <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={(p) => onPageChange(p)} />
      )}
    </>
  );
};
